import React, { useCallback, useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import {
  FaNetworkWired, FaCheckCircle, FaTimesCircle, FaPlus, FaTrash, FaUnlock,
} from "react-icons/fa";
import { Card, CardBody, PageHeader, Button, Spinner } from "../../components/ui";
import { useModal } from "../../context/ModalContext";

const emptyProvider = { name: "", proxyUrl: "" };

function TestStatus({ success, message }) {
  if (success === null || success === undefined) return null;
  return (
    <div className={`mt-3 flex items-center gap-2 rounded-lg border p-3 text-sm ${
      success ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"
    }`}>
      {success ? <FaCheckCircle /> : <FaTimesCircle />} {message}
    </div>
  );
}

export default function AdminProxyPool() {
  const { alert, confirm, prompt } = useModal();
  const [tier, setTier] = useState("free");
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entryText, setEntryText] = useState("");
  const [providerForm, setProviderForm] = useState(emptyProvider);
  const [addingProvider, setAddingProvider] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [draftTest, setDraftTest] = useState(null);
  const [form, setForm] = useState({
    name: "Free QR queue",
    enabled: false,
    mode: "gateway",
    stickyHours: 24,
  });

  const withTier = (path) => `${path}${path.includes("?") ? "&" : "?"}tier=${tier}`;

  const applyPool = useCallback((data) => {
    setPool((previous) => ({
      ...data,
      addonStats: data.addonStats || previous?.addonStats,
    }));
    setForm({
      name: data.name || (tier === "free" ? "Free QR queue" : "Premium proxy queue"),
      enabled: !!data.enabled,
      mode: data.mode || "gateway",
      stickyHours: data.stickyHours || 24,
    });
  }, [tier]);

  useEffect(() => {
    setLoading(true);
    setPool(null);
    api.get(`/api/admin/proxy-pool?tier=${tier}`)
      .then((res) => applyPool(res.data.data))
      .catch((err) => alert(err.response?.data?.message || "Failed to load proxy pool", "error"))
      .finally(() => setLoading(false));
  }, [alert, applyPool, tier]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(withTier("/api/admin/proxy-pool"), { ...form, tier });
      applyPool(res.data.data);
      alert("Proxy-pool settings saved", "success");
    } catch (err) {
      alert(err.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const testDraftProvider = async () => {
    if (!providerForm.proxyUrl.trim()) return;
    setTestingProvider("draft");
    setDraftTest(null);
    try {
      const res = await api.post(withTier("/api/admin/proxy-pool/test"), { proxyUrl: providerForm.proxyUrl.trim(), tier });
      setDraftTest({
        success: true,
        message: `Working${res.data.observedIp ? ` — outbound IP ${res.data.observedIp}` : ""} (${res.data.elapsedMs}ms)`,
      });
    } catch (err) {
      setDraftTest({ success: false, message: err.response?.data?.message || "Proxy test failed" });
    } finally {
      setTestingProvider(null);
    }
  };

  const addProvider = async () => {
    if (!providerForm.proxyUrl.trim()) return;
    setAddingProvider(true);
    try {
      const res = await api.post(withTier("/api/admin/proxy-pool/providers"), {
        tier,
        name: providerForm.name.trim(),
        proxyUrl: providerForm.proxyUrl.trim(),
      });
      applyPool(res.data.data);
      setProviderForm(emptyProvider);
      setDraftTest(null);
      alert("Provider added. Test it before enabling the pool for new sessions.", "success");
    } catch (err) {
      alert(err.response?.data?.message || "Could not add provider", "error");
    } finally {
      setAddingProvider(false);
    }
  };

  const testProvider = async (id) => {
    setTestingProvider(id);
    try {
      const res = await api.post(withTier(`/api/admin/proxy-pool/providers/${id}/test`));
      applyPool(res.data.data);
    } catch (err) {
      if (err.response?.data?.data) applyPool(err.response.data.data);
      alert(err.response?.data?.message || "Provider test failed", "error");
    } finally {
      setTestingProvider(null);
    }
  };

  const testLegacyProvider = async () => {
    setTestingProvider("legacy");
    try {
      const res = await api.post(withTier("/api/admin/proxy-pool/test"));
      alert(
        `Primary provider works${res.data.observedIp ? ` — outbound IP ${res.data.observedIp}` : ""} (${res.data.elapsedMs}ms)`,
        "success"
      );
    } catch (err) {
      alert(err.response?.data?.message || "Primary provider test failed", "error");
    } finally {
      setTestingProvider(null);
    }
  };

  const toggleProvider = async (provider) => {
    try {
      const res = await api.patch(withTier(`/api/admin/proxy-pool/providers/${provider.id}`), { enabled: !provider.enabled, tier });
      applyPool(res.data.data);
    } catch (err) {
      alert(err.response?.data?.message || "Could not update provider", "error");
    }
  };

  const replaceProviderUrl = async (provider) => {
    const next = await prompt(`Replace the proxy URL for ${provider.name}`, {
      placeholder: "socks5://user-session-{session}:password@gateway:1080",
      inputType: "password",
      autoComplete: "new-password",
      description: `Current: ${provider.proxyUrlMasked}. The new URL will be encrypted at rest.`,
      confirmText: "Replace",
    });
    if (!next?.trim()) return;
    try {
      const res = await api.patch(withTier(`/api/admin/proxy-pool/providers/${provider.id}`), { proxyUrl: next.trim(), tier });
      applyPool(res.data.data);
      alert("Provider URL replaced. Run a test before using it.", "success");
    } catch (err) {
      alert(err.response?.data?.message || "Could not replace provider URL", "error");
    }
  };

  const removeProvider = async (provider) => {
    const ok = await confirm(`Remove ${provider.name}? Existing sessions keep their saved proxy until reconnected.`, {
      danger: true,
      confirmText: "Remove",
    });
    if (!ok) return;
    const res = await api.delete(withTier(`/api/admin/proxy-pool/providers/${provider.id}`));
    applyPool(res.data.data);
  };

  const removeLegacyProvider = async () => {
    const ok = await confirm("Remove the original primary provider? Existing sessions keep their saved proxy.", {
      danger: true,
      confirmText: "Remove",
    });
    if (!ok) return;
    const res = await api.put(withTier("/api/admin/proxy-pool"), { gatewayUrl: "", tier });
    applyPool(res.data.data);
  };

  const addEntries = async () => {
    if (!entryText.trim()) return;
    try {
      const res = await api.post(withTier("/api/admin/proxy-pool/entries"), { urls: entryText, tier });
      applyPool(res.data.data);
      setEntryText("");
      alert(`Added ${res.data.added} IP(s) to the pool`, "success");
    } catch (err) {
      alert(err.response?.data?.message || "Could not add proxies", "error");
    }
  };

  const removeEntry = async (id) => {
    const ok = await confirm("Remove this IP from the pool?", { danger: true, confirmText: "Remove" });
    if (!ok) return;
    const res = await api.delete(withTier(`/api/admin/proxy-pool/entries/${id}`));
    applyPool(res.data.data);
  };

  const releaseEntry = async (id) => {
    const res = await api.post(withTier(`/api/admin/proxy-pool/entries/${id}/release`));
    applyPool(res.data.data);
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-sm text-ink-muted"><Spinner /> Loading proxy pool…</div>;
  }

  return (
    <div>
      <PageHeader
        title="WhatsApp proxy queues"
        subtitle="Free QR connections use the free queue. Active $5 managed-proxy seats are routed to the premium queue."
      />

      <div className="mb-6 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setTier("free")}
          className={`rounded-xl border p-4 text-left transition-colors ${tier === "free" ? "border-brand bg-brand/5" : "border-surface-border bg-surface-card"}`}
        >
          <strong className="text-ink-primary">Free QR queue</strong>
          <p className="mt-1 text-xs text-ink-muted">Automatic fallback for every eligible QR-session customer without a premium seat.</p>
        </button>
        <button
          type="button"
          onClick={() => setTier("premium")}
          className={`rounded-xl border p-4 text-left transition-colors ${tier === "premium" ? "border-brand bg-brand/5" : "border-surface-border bg-surface-card"}`}
        >
          <strong className="text-ink-primary">Premium $5 queue</strong>
          <p className="mt-1 text-xs text-ink-muted">Reserved for numbers with active managed-proxy add-on seats.</p>
        </button>
      </div>

      {tier === "premium" && pool?.addonStats && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="rounded-lg border border-surface-border bg-surface-card p-4"><p className="text-xs text-ink-muted">Active add-ons</p><p className="text-xl font-semibold text-ink-primary">{pool.addonStats.activeSubscriptions}</p></div>
          <div className="rounded-lg border border-surface-border bg-surface-card p-4"><p className="text-xs text-ink-muted">Seats sold</p><p className="text-xl font-semibold text-ink-primary">{pool.addonStats.seatsSold}</p></div>
          <div className="rounded-lg border border-surface-border bg-surface-card p-4"><p className="text-xs text-ink-muted">Seats assigned</p><p className="text-xl font-semibold text-ink-primary">{pool.addonStats.assignedSeats}</p></div>
          <div className="rounded-lg border border-surface-border bg-surface-card p-4"><p className="text-xs text-ink-muted">Canceling</p><p className="text-xl font-semibold text-ink-primary">{pool.addonStats.cancelingSubscriptions}</p></div>
        </div>
      )}

      <Card className="mb-6">
        <CardBody>
          <form onSubmit={save} className="max-w-3xl space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="input-group">
                <label className="input-label">Pool name</label>
                <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Sticky hours</label>
                <input className="input" type="number" min="1" value={form.stickyHours} onChange={(e) => setForm({ ...form, stickyHours: Number(e.target.value) })} />
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Mode</label>
              <select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="gateway">Gateway providers — multiple providers with a unique sticky session per number</option>
                <option value="list">Dedicated list — one unused proxy endpoint per session</option>
              </select>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled — assign from the {tier} queue on connect
            </label>
            <div className={`rounded-lg border p-3 text-sm ${tier === "premium" ? "border-violet-200 bg-violet-50 text-violet-900" : "border-blue-200 bg-blue-50 text-blue-900"}`}>
              {tier === "premium"
                ? "Only active $5 managed-proxy seats use this queue. If premium capacity is unavailable, the number falls back to the free queue instead of failing."
                : "Starter and other QR-enabled plans use this queue automatically when no premium seat is attached. Credentials remain hidden from tenants."}
            </div>
            <Button type="submit" variant="primary" loading={saving}>{saving ? "Saving…" : "Save pool settings"}</Button>
          </form>
        </CardBody>
      </Card>

      {form.mode === "gateway" && (
        <Card className="mb-6">
          <CardBody>
            <div className="mb-5">
              <h3 className="flex items-center gap-2 font-semibold text-ink-primary"><FaNetworkWired /> Gateway providers</h3>
              <p className="mt-1 text-sm text-ink-muted">
                {pool?.enabledProviderCount || 0} enabled / {pool?.providerCount || 0} configured. New sessions are distributed consistently across enabled providers.
              </p>
            </div>

            <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              Put <code className="font-mono">{"{session}"}</code> in the provider username only when that provider supports sticky-session usernames. Without it, Walytic preserves the username exactly. URLs are encrypted and passwords are never returned to the browser.
            </div>

            {pool?.hasGatewayUrl && (
              <div className="mb-3 rounded-lg border border-surface-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><strong>Primary provider</strong><span className="badge-info">Existing</span></div>
                    <div className="mt-1 truncate font-mono text-xs text-ink-muted">{pool.gatewayUrlMasked}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="secondary" loading={testingProvider === "legacy"} onClick={testLegacyProvider}>Test</Button>
                    <Button type="button" variant="danger" onClick={removeLegacyProvider}>Remove</Button>
                  </div>
                </div>
              </div>
            )}

            {(pool?.providers || []).map((provider) => (
              <div key={provider.id} className="mb-3 rounded-lg border border-surface-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <strong>{provider.name}</strong>
                      <span className={provider.enabled ? "badge-success" : "badge-warning"}>{provider.enabled ? "Enabled" : "Disabled"}</span>
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-ink-muted">{provider.proxyUrlMasked}</div>
                    {provider.lastTestedAt && (
                      <div className={`mt-2 text-xs ${provider.lastTestSuccess ? "text-green-700" : "text-red-700"}`}>
                        {provider.lastTestSuccess
                          ? `Last test passed${provider.lastObservedIp ? ` · IP ${provider.lastObservedIp}` : ""}${provider.lastLatencyMs ? ` · ${provider.lastLatencyMs}ms` : ""}`
                          : `Last test failed · ${provider.lastError || "Connection failed"}`}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" loading={testingProvider === provider.id} onClick={() => testProvider(provider.id)}>Test</Button>
                    <Button type="button" variant="secondary" onClick={() => replaceProviderUrl(provider)}>Replace URL</Button>
                    <Button type="button" variant="secondary" onClick={() => toggleProvider(provider)}>{provider.enabled ? "Disable" : "Enable"}</Button>
                    <Button type="button" variant="danger" onClick={() => removeProvider(provider)}><FaTrash size={12} /></Button>
                  </div>
                </div>
              </div>
            ))}

            {!pool?.hasGatewayUrl && !(pool?.providers || []).length && (
              <div className="mb-5 rounded-lg border border-dashed border-surface-border p-6 text-center text-sm text-ink-muted">No gateway providers yet</div>
            )}

            <div className="mt-6 border-t border-surface-border pt-5">
              <h4 className="mb-3 font-semibold text-ink-primary">Add another provider</h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[220px_1fr]">
                <input className="input" placeholder="Provider name" value={providerForm.name} onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })} />
                <input
                  className="input font-mono text-sm"
                  type="password"
                  autoComplete="off"
                  placeholder="socks5://user-session-{session}:password@gateway:1080"
                  value={providerForm.proxyUrl}
                  onChange={(e) => { setProviderForm({ ...providerForm, proxyUrl: e.target.value }); setDraftTest(null); }}
                />
              </div>
              <div className="mt-3 flex gap-3">
                <Button type="button" variant="secondary" loading={testingProvider === "draft"} disabled={!providerForm.proxyUrl.trim()} onClick={testDraftProvider}>Test before adding</Button>
                <Button type="button" variant="primary" loading={addingProvider} disabled={!providerForm.proxyUrl.trim()} onClick={addProvider}><FaPlus className="mr-2" size={12} /> Add provider</Button>
              </div>
              <TestStatus success={draftTest?.success} message={draftTest?.message} />
            </div>
          </CardBody>
        </Card>
      )}

      {form.mode === "list" && (
        <Card>
          <CardBody>
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-ink-primary"><FaNetworkWired /> Dedicated IPs</h3>
            <p className="mb-4 text-sm text-ink-muted">
              {pool?.assignedCount || 0} in use / {pool?.totalEntries || 0} in pool. One line per proxy; add an optional label after <span className="font-mono">#</span>.
            </p>
            <textarea
              className="input mb-3 min-h-[120px] font-mono text-sm"
              placeholder={"socks5://user:pass@1.2.3.4:1080 # Mumbai\nsocks5://user:pass@5.6.7.8:1080 # Dubai"}
              value={entryText}
              onChange={(e) => setEntryText(e.target.value)}
            />
            <Button type="button" variant="secondary" onClick={addEntries} disabled={!entryText.trim()}><FaPlus className="mr-2" size={12} /> Add to pool</Button>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-ink-muted"><th className="py-2">IP / URL</th><th>Label</th><th>Assigned session</th><th /></tr></thead>
                <tbody>
                  {(pool?.entries || []).map((entry) => (
                    <tr key={entry.id} className="border-t border-surface-border">
                      <td className="py-2 font-mono text-xs">{entry.proxyUrlMasked}</td>
                      <td>{entry.label || "—"}</td>
                      <td className="font-mono text-xs">{entry.assignedSessionId || "free"}</td>
                      <td className="whitespace-nowrap text-right">
                        {entry.assignedSessionId && <button type="button" className="mr-3 text-xs text-ink-muted" onClick={() => releaseEntry(entry.id)}><FaUnlock className="mr-1 inline" /> Release</button>}
                        <button type="button" className="text-red-600" onClick={() => removeEntry(entry.id)}><FaTrash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                  {!(pool?.entries || []).length && <tr><td colSpan="4" className="py-6 text-center text-ink-muted">No IPs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
